// === CART REMOVE BUTTON ===========================================
class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();

      const cartItems =
        this.closest("cart-items") || this.closest("cart-drawer-items");

      if (!cartItems || typeof cartItems.updateQuantity !== "function") return;

      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

if (!customElements.get("cart-remove-button")) {
  customElements.define("cart-remove-button", CartRemoveButton);
}

// === CART ITEMS ====================================================
class CartItems extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement =
      document.getElementById("shopping-cart-line-item-status") ||
      document.getElementById("CartDrawer-LineItemStatus");

    // Init shipping bar direct bij load
    if (document.querySelector(".cart-shipping__progress")) {
      this.cartShipping();
    }

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener("change", debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  /**
   * (Re)calculate and render free-shipping bar
   * Fix: gebruik euro → cent conversie voor formatMoney
   */
  cartShipping() {
    const shippingEl = document.querySelector(".cart-shipping");
    const progressBar = document.querySelector(".cart-shipping__progress-current");
    const defaultMessageEl = document.querySelector(
      ".cart-shipping__message_default"
    );
    const successMessageEl = document.querySelector(
      ".cart-shipping__message_success"
    );

    if (!shippingEl || !progressBar || !defaultMessageEl || !successMessageEl) {
      return;
    }

    // vorige width bewaren voor eventuele animatie
    const previousWidth = getComputedStyle(progressBar).getPropertyValue("width");
    document.documentElement.style.setProperty("--progress-prev", previousWidth);

    const rate =
      (window.Shopify &&
        Shopify.currency &&
        typeof Shopify.currency.rate === "number" &&
        Shopify.currency.rate) ||
      1;

    const minSpendAttr = shippingEl.dataset.minSpend; // "58"
    const totalAttr = shippingEl.dataset.total;       // "47.99"
    const messageTemplate =
      shippingEl.dataset.message || defaultMessageEl.textContent || "";

    const minSpend = parseFloat(minSpendAttr) || 0; // euro
    const total = parseFloat(totalAttr) || 0;       // euro

    const minTotal = minSpend * rate; // euro (bij EUR: zelfde als minSpend)
    if (!minTotal || minTotal <= 0) return;

    this.minSpend = minSpend;
    this.minTotal = minTotal;
    this.total = total;

    // Progress (0–100)
    let progress = (total / minTotal) * 100;
    progress = Math.max(0, Math.min(100, progress));
    this.progress = progress;

    progressBar.style.width = `${progress}%`;

    // Resterend bedrag
    if (total < minTotal) {
      const amountEuro = minTotal - total; // euro
      const amountCents = Math.round(amountEuro * 100); // centen voor formatMoney

      let formattedAmount;

      if (typeof window.formatMoney === "function") {
        formattedAmount = window.formatMoney(amountCents);
      } else {
        // Fallback formatting
        const currency = shippingEl.dataset.currency || "EUR";
        formattedAmount = amountEuro.toLocaleString(undefined, {
          style: "currency",
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }

      const message = messageTemplate.replace("||amount||", formattedAmount);

      defaultMessageEl.textContent = message;
      defaultMessageEl.classList.add("active");
      successMessageEl.classList.remove("active");
    } else {
      defaultMessageEl.classList.remove("active");
      successMessageEl.classList.add("active");
    }
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === "cart-items") return;
        this.onCartUpdate();
      }
    );

    initButtonsAnimation(this.parentElement);
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute("name")
    );
  }

  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=main-cart-items`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html");
        const sourceQty = html.querySelector("cart-items");
        if (!sourceQty) return;
        this.innerHTML = sourceQty.innerHTML;
      })
      .catch((e) => console.error(e));
  }

  getSectionsToRender() {
    return [
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items").dataset.id,
        selector: ".js-contents",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: ".shopify-section",
      },
      {
        id: "main-cart-footer",
        section: document.getElementById("main-cart-footer").dataset.id,
        selector: ".js-contents-totals",
      },
      {
        id: "main-cart-shipping",
        section:
          document.getElementById("main-cart-shipping").dataset.id || null,
        selector: ".js-contents-shipping",
      },
    ];
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    this.querySelectorAll(".quantity__button").forEach((button) =>
      button.classList.add("disabled")
    );

    // Reset "sold out" state op product cards
    const addToCartButtons = document.querySelectorAll(
      '.card--product .card__add-to-cart button[name="add"]'
    );
    if (addToCartButtons.length) {
      addToCartButtons.forEach((button) => {
        button.setAttribute("aria-disabled", false);
        const labelSpan = button.querySelector("span");
        const soldOutMessage = button.querySelector(".sold-out-message");
        if (labelSpan) labelSpan.classList.remove("hidden");
        if (soldOutMessage) soldOutMessage.classList.add("hidden");
      });
    }

    // Progress vorige width opslaan voor animatie
    const progressEl = document.querySelector(".cart-shipping__progress-current");
    if (progressEl) {
      const progressPrev = getComputedStyle(progressEl).getPropertyValue("width");
      document.documentElement.style.setProperty("--progress-prev", progressPrev);
    }

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll(".cart-item");

        if (parsedState.errors) {
          if (quantityElement) {
            quantityElement.value = quantityElement.getAttribute("value");
          }
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle("is-empty", parsedState.item_count === 0);

        const cartDrawerWrapper = document.querySelector("cart-drawer");
        const cartFooter = document.getElementById("main-cart-footer");
        const cartPageWrapper = document.querySelector(".main-cart__wrapper");

        if (cartFooter) {
          cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
        }
        if (cartDrawerWrapper) {
          cartDrawerWrapper.classList.toggle(
            "is-empty",
            parsedState.item_count === 0
          );
        }
        if (cartPageWrapper) {
          cartPageWrapper.classList.toggle(
            "is-empty",
            parsedState.item_count === 0
          );
        }

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document
              .getElementById(section.id)
              .querySelector(section.selector) ||
            document.getElementById(section.id);

          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });

        const updatedItem = parsedState.items[line - 1];
        const updatedValue = updatedItem ? updatedItem.quantity : undefined;

        let message = "";
        if (
          items.length === parsedState.items.length &&
          quantityElement &&
          updatedValue !== parseInt(quantityElement.value, 10)
        ) {
          if (typeof updatedValue === "undefined") {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace(
              "[quantity]",
              updatedValue
            );
          }
        }

        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) ||
          document.getElementById(`CartDrawer-Item-${line}`);

        const cartDrawer = document.querySelector("cart-drawer");

        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawer
            ? trapFocus(cartDrawer, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawer) {
          trapFocus(
            cartDrawer.querySelector(".drawer__inner-empty"),
            cartDrawer.querySelector("a")
          );
        } else if (document.querySelector(".cart-item") && cartDrawer) {
          trapFocus(cartDrawer, document.querySelector(".cart-item__name"));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-items" });

        const popularSlider = document.querySelector(
          "cart-popular-products-slider"
        );
        if (
          popularSlider &&
          typeof popularSlider.init === "function"
        ) {
          popularSlider.init();
        }
      })
      .catch(() => {
        this.querySelectorAll(".loading-overlay").forEach((overlay) =>
          overlay.classList.add("hidden")
        );
        this.querySelectorAll(".quantity__button").forEach((button) =>
          button.classList.remove("disabled")
        );
        const errors =
          document.getElementById("cart-errors") ||
          document.getElementById("CartDrawer-CartErrors");
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.querySelectorAll(".quantity__button").forEach((button) =>
          button.classList.remove("disabled")
        );
        if (document.querySelector(".cart-shipping__progress")) {
          this.cartShipping();
        }
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);

    if (lineItemError) {
      lineItemError.querySelector(".cart-item__error-text").innerHTML = message;
    }

    this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus =
      document.getElementById("cart-live-region-text") ||
      document.getElementById("CartDrawer-LiveRegionText");

    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const cartLine =
      this.querySelector(`#CartItem-${line}`) ||
      this.querySelector(`#CartDrawer-Item-${line}`);

    if (cartLine) {
      cartLine.classList.add("disabled");
      const loadingElement = cartLine.querySelector(".loading-overlay");
      if (loadingElement) loadingElement.classList.remove("hidden");
    }

    const cartFooter =
      document.querySelector(".cart__footer") ||
      document.querySelector(".cart-drawer__footer");

    if (cartFooter) {
      const subtotalValue = cartFooter.querySelector(
        ".totals__subtotal-value > span"
      );
      if (subtotalValue) subtotalValue.classList.add("disabled");
      const loadingElement = cartFooter.querySelector(".loading-overlay");
      if (loadingElement) loadingElement.classList.remove("hidden");
    }

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute("aria-hidden", false);
  }

  disableLoading(line) {
    const cartLine =
      this.querySelector(`#CartItem-${line}`) ||
      this.querySelector(`#CartDrawer-Item-${line}`);

    if (cartLine) {
      cartLine.classList.remove("disabled");
      const loadingElement = cartLine.querySelector(".loading-overlay");
      if (loadingElement) loadingElement.classList.add("hidden");
    }

    const cartFooter =
      document.querySelector(".cart__footer") ||
      document.querySelector(".cart-drawer__footer");

    if (cartFooter) {
      const subtotalValue = cartFooter.querySelector(
        ".totals__subtotal-value > span"
      );
      if (subtotalValue) subtotalValue.classList.remove("disabled");
      const loadingElement = cartFooter.querySelector(".loading-overlay");
      if (loadingElement) loadingElement.classList.add("hidden");
    }
  }
}

if (!customElements.get("cart-items")) {
  customElements.define("cart-items", CartItems);
}

// === CART NOTE =====================================================
class CartNote extends HTMLElement {
  constructor() {
    super();

    this.spinnerIcon = this.querySelector(".cart__note-loading");
    this.successIcon = this.querySelector(".cart__note-success");
    this.textarea = this.querySelector("textarea");
    this.cartButtons = document.querySelector(".cart__ctas");
    this.drawerButtons = document.querySelector(".cart-drawer__buttons");

    if (!this.textarea) return;

    this.textarea.addEventListener(
      "input",
      debounce((event) => {
        if (this.spinnerIcon) this.spinnerIcon.style.display = "flex";
        if (this.successIcon) this.successIcon.style.display = "none";
        if (this.cartButtons) {
          this.cartButtons.style.pointerEvents = "none";
          this.cartButtons.style.opacity = "0.7";
        }
        if (this.drawerButtons) {
          this.drawerButtons.style.pointerEvents = "none";
          this.drawerButtons.style.opacity = "0.7";
        }

        const body = JSON.stringify({ note: event.target.value });

        fetch(`${routes.cart_update_url}`, {
          ...fetchConfig(),
          ...{ body },
        })
          .then((response) => {
            if (!response.ok) throw new Error("Network response was not ok");
            return response.json();
          })
          .then(() => {
            if (this.successIcon) this.successIcon.style.display = "flex";
            setTimeout(() => {
              if (this.successIcon) this.successIcon.style.display = "none";
            }, 2000);
          })
          .catch((error) => {
            console.error("There was a problem with the fetch operation:", error);
          })
          .finally(() => {
            if (this.spinnerIcon) this.spinnerIcon.style.display = "none";
            if (this.cartButtons) this.cartButtons.removeAttribute("style");
            if (this.drawerButtons) this.drawerButtons.removeAttribute("style");
          });
      }, 500)
    );
  }
}

if (!customElements.get("cart-note")) {
  customElements.define("cart-note", CartNote);
}

// === CART POPULAR PRODUCTS SLIDER =================================
class CartPopularProductsSlider extends HTMLElement {
  constructor() {
    super();
    window.addEventListener("load", this.init.bind(this));
  }

  init() {
    const sliderEl = this.querySelector(
      ".cart-popular-products-slider__window"
    );
    if (!sliderEl) return;
    if (sliderEl.swiper) sliderEl.swiper.destroy();
    if (this.getAttribute("data-cart-is-empty") === "true") return;

    const nextBtn = this.querySelector(
      ".cart-popular-products-slider__button--next"
    );
    const prevBtn = this.querySelector(
      ".cart-popular-products-slider__button--prev"
    );
    const paginationEl = this.querySelector(".swiper-pagination");

    const swiperSettings = {
      slidesPerView: 1.1,
      spaceBetween: 8,
      speed: 400,
      allowTouchMove: true,
      loop: true,
      loopPreventsSliding: false,
      navigation: {
        nextEl: nextBtn,
        prevEl: prevBtn,
        disabledClass: "swiper-button-disabled",
      },
      pagination: {
        el: paginationEl,
        type: "bullets",
        clickable: true,
      },
    };

    // eslint-disable-next-line no-undef
    new Swiper(sliderEl, swiperSettings);
  }
}

if (!customElements.get("cart-popular-products-slider")) {
  customElements.define(
    "cart-popular-products-slider",
    CartPopularProductsSlider
  );
}
